import * as React from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import {
  fetchUserPermissionsDetail,
  getAllDirectPermissionIds,
  getSmartDirectPermissions,
  getSmartRoleGroups,
  type UserPermissionsDetail,
} from "../../lib/api/user-permissions-detail";
import * as adminUsersApi from "../../lib/api/admin-users";
import {
  collapseDirectPermissionIds,
  dedupePermissionCatalog,
} from "../../lib/permission-name-canonical";
import {
  buildDirectPermissionGroups,
  collectEffectiveByName,
} from "../../lib/permission-groups";
import { PermissionGroupGrid } from "../permissions/PermissionGroup";
import { ConfirmModal } from "../../../ui/components/ConfirmModal";
import { ApiClientError } from "../../lib/api/client";
import { ProApiClientError } from "../../lib/api/pro-api.client";
import { useI18n } from "../../lib/i18n";
import * as styles from "./UserDetailPage.css";

function getErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiClientError || e instanceof ProApiClientError) {
    return e.message;
  }
  if (e instanceof Error) return e.message;
  return fallback;
}

function CollapsiblePermissionSection({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  const contentId = React.useId();

  return (
    <section className={styles.permissionSection}>
      <button
        type="button"
        className={styles.permissionSectionToggle}
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={styles.permissionSectionChevron} aria-hidden>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>
        <span className={styles.permissionGroupTitle}>{title}</span>
      </button>
      {open ? (
        <div id={contentId} className={styles.permissionSectionContent}>
          {children}
        </div>
      ) : null}
    </section>
  );
}

export function UserPermissionsSection({
  userId,
  token,
  userLabel = "this user",
  refreshKey,
}: {
  userId: string;
  token: string;
  userLabel?: string;
  /** Bump this to force a reload (e.g. after global roles change elsewhere on the page). */
  refreshKey?: number;
}) {
  const { t } = useI18n();
  const [detail, setDetail] = React.useState<UserPermissionsDetail | null>(
    null,
  );
  const [platformCatalog, setPlatformCatalog] = React.useState<
    adminUsersApi.PermissionSummary[]
  >([]);
  const [smartCatalog, setSmartCatalog] = React.useState<
    adminUsersApi.PermissionSummary[]
  >([]);
  const [directPermissionIds, setDirectPermissionIds] = React.useState<
    Set<string>
  >(() => new Set());
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [pendingPermissionToggle, setPendingPermissionToggle] = React.useState<{
    permissionId: string;
    permissionName: string;
    enabled: boolean;
  } | null>(null);
  const [confirmError, setConfirmError] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const [permDetail, catalog, smart] = await Promise.all([
      fetchUserPermissionsDetail(token, userId),
      adminUsersApi.listLionProPlatformPermissions(token),
      adminUsersApi.listLionSmartPermissions(token),
    ]);
    setDetail(permDetail);
    setPlatformCatalog(catalog);
    setSmartCatalog(smart);
    const combinedCatalog = [...catalog, ...smart];
    setDirectPermissionIds(
      collapseDirectPermissionIds(
        getAllDirectPermissionIds(permDetail),
        combinedCatalog,
      ),
    );
  }, [token, userId]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    reload()
      .catch((e) => {
        if (!cancelled) {
          setError(getErrorMessage(e, "Failed to load permissions"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reload, refreshKey]);

  const requestDirectToggle = React.useCallback(
    (permissionId: string, permissionName: string, enabled: boolean) => {
      if (!permissionId || saving) return;
      setConfirmError(null);
      setPendingPermissionToggle({ permissionId, permissionName, enabled });
    },
    [saving],
  );

  const handleDirectToggleConfirm = React.useCallback(async () => {
    if (!pendingPermissionToggle) return;
    const { permissionId, enabled } = pendingPermissionToggle;
    const next = new Set(directPermissionIds);
    if (enabled) {
      next.add(permissionId);
    } else {
      next.delete(permissionId);
    }
    setSaving(true);
    setConfirmError(null);
    setError(null);
    try {
      await adminUsersApi.updateUserDirectPermissions(
        token,
        userId,
        [...next],
      );
      await reload();
      setPendingPermissionToggle(null);
    } catch (e) {
      const message = getErrorMessage(e, "Failed to update permissions");
      setConfirmError(message);
      setError(message);
    } finally {
      setSaving(false);
    }
  }, [pendingPermissionToggle, directPermissionIds, token, userId, reload]);

  const pendingPermissionCopy = pendingPermissionToggle
    ? pendingPermissionToggle.enabled
      ? {
          title: "Grant permission?",
          body: `Grant the ${pendingPermissionToggle.permissionName} permission directly to ${userLabel}?`,
          confirmLabel: "Grant permission",
          confirmColor: "primary" as const,
        }
      : {
          title: "Revoke permission?",
          body: `Revoke direct ${pendingPermissionToggle.permissionName} permission from ${userLabel}?`,
          confirmLabel: "Revoke permission",
          confirmColor: "danger" as const,
        }
    : null;

  if (loading) {
    return (
      <Text level="body-medium" style={{ color: "#6B7280" }}>
        Loading permissions…
      </Text>
    );
  }

  if (error && !detail) {
    return <Callout variant="error">{error}</Callout>;
  }

  if (!detail) {
    return <p className={styles.orgEmpty}>{t("pro.users.unableToLoadPermissions")}</p>;
  }

  const globalEffectiveItems = [
    ...detail.lionProGlobal.direct,
    ...detail.lionProGlobal.fromRoles.flatMap((g) => g.permissions),
    ...getSmartDirectPermissions(detail),
    ...getSmartRoleGroups(detail).flatMap((g) => g.permissions),
  ];
  const globalPermissionGroups = buildDirectPermissionGroups(
    dedupePermissionCatalog([...platformCatalog, ...smartCatalog]),
    directPermissionIds,
    collectEffectiveByName(globalEffectiveItems),
  );

  const showEmpty = globalPermissionGroups.length === 0;

  return (
    <>
      {error && (
        <div style={{ marginBottom: 12 }}>
          <Callout variant="error">{error}</Callout>
        </div>
      )}

      {showEmpty ? (
        <p className={styles.orgEmpty}>{t("pro.users.noGlobalPermissions")}</p>
      ) : (
        <div className={styles.permissionsLayout}>
          <CollapsiblePermissionSection title={t("pro.users.globalPermissions")}>
            <p className={styles.permissionGroupHint}>
              Platform administration and Smart ecosystem access via global
              roles or direct assignment.
            </p>
            <PermissionGroupGrid
              groups={globalPermissionGroups}
              onActionToggle={requestDirectToggle}
              disabled={saving}
            />
          </CollapsiblePermissionSection>
        </div>
      )}
      {pendingPermissionToggle && (
        <ConfirmModal
          open={Boolean(pendingPermissionToggle)}
          title={pendingPermissionCopy!.title}
          body={pendingPermissionCopy!.body}
          confirmLabel={pendingPermissionCopy!.confirmLabel}
          confirmColor={pendingPermissionCopy!.confirmColor}
          loading={saving}
          error={confirmError}
          onCancel={() => {
            setPendingPermissionToggle(null);
            setConfirmError(null);
          }}
          onConfirm={() => void handleDirectToggleConfirm()}
        />
      )}
    </>
  );
}
